import { useCallback } from 'react';

import { useActorRef } from '@xstate5/react';
import { type ActorRefFrom, type MachineImplementationsFrom } from 'xstate5';

import { datadog } from '@sg/expo-datadog-client';
import { useAppState } from '@sg/garnish';
import { otaMachine } from '@sg/ota-installer';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { otaLogger } from './helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const useInitOtaActor = (): ActorRefFrom<typeof otaMachine> => {
  const appState = useAppState();

  const pollingIntervalInMinutes = useFeatureFlag('CELS-4466-order-in-app-ota-polling-interval');

  // ─── Guards ──────────────────────────────────────────────────────────

  const checkIfAppIsReady = useCallback(() => appState === 'active', [appState]);

  // ─── Refs ────────────────────────────────────────────────────────────

  const actorRef = useActorRef(
    otaMachine.provide({
      actions: otaMachineActions,
      delays: { POLLING_INTERVAL: pollingIntervalInMinutes * 60 * 1000 },
      guards: { 'check-if-app-is-ready': checkIfAppIsReady },
    }),
    { input: { logger: otaLogger, shouldUseLaunchTimeGate: true } },
  );

  // ─────────────────────────────────────────────────────────────────────

  return actorRef;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function addOtaDatadogAction(name: string) {
  try {
    await datadog.addAction(name);
  } catch {}
}

// ─── Constants ───────────────────────────────────────────────────────────────

const otaMachineActions: OtaMachineActions = {
  onLaunchGateEntered: () => addOtaDatadogAction('[OTA] Launch Gate Entered'),
  onLaunchGateTimedOut: () => addOtaDatadogAction('[OTA] Launch Gate Timed Out'),
  onLaunchFetchStarted: () => addOtaDatadogAction('[OTA] Launch Fetch Started'),
  onLaunchFetchNoUpdate: () => addOtaDatadogAction('[OTA] Launch Fetch No Update'),
  onLaunchFetchUpdateFound: () => addOtaDatadogAction('[OTA] Launch Fetch Update Found'),
  onLaunchFetchFailed: () => addOtaDatadogAction('[OTA] Launch Fetch Failed'),
  onLaunchFetchTimedOut: () => addOtaDatadogAction('[OTA] Launch Fetch Timed Out'),
  onUpdateFound: () => addOtaDatadogAction('[OTA] Update Found'),
  onRestartDeferred: () => addOtaDatadogAction('[OTA] Restart Deferred'),
  onRestartStarted: () => addOtaDatadogAction('[OTA] Restart Started'),
  onRestartSucceeded: () => addOtaDatadogAction('[OTA] Restart Succeeded'),
  onRestartFailed: () => addOtaDatadogAction('[OTA] Restart Failed'),
};

// ─── Types ───────────────────────────────────────────────────────────────────

type OtaMachineActions = NonNullable<MachineImplementationsFrom<typeof otaMachine>['actions']>;
